/**
 * useEVScenarios Hook
 *
 * Encapsulates scenario list, selection, CRUD, save status, and single-scenario simulation.
 *
 * FIX B2: handleScenarioChange uses functional updater with NO selectedScenario in deps.
 * FIX B3: All callbacks use functional updaters to avoid stale closures.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@vortx/shared-ui';
import * as evService from '@vortx/shared-ui/src/services/evProjectService';

export function useEVScenarios(projectId, currentProject) {
    const { currentUser } = useAuth();

    const [scenarios, setScenarios] = useState([]);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [simulatingScenarioId, setSimulatingScenarioId] = useState(null);

    // Scenario save status
    const [saveStatus, setSaveStatus] = useState('idle');
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [saveError, setSaveError] = useState(null);

    // Track which project we auto-created for (prevent duplicate auto-creates)
    const autoCreatedForRef = useRef(null);

    // SOC trajectories are not persisted to Firestore (too large).
    // Capture from simulation response and hold in a ref keyed by scenario ID.
    const socDataRef = useRef({});

    // Load scenarios when project is loaded
    useEffect(() => {
        // Always clear stale data immediately when dependencies change
        setScenarios([]);
        setSelectedScenario(null);

        if (!projectId || !currentUser || !currentProject) {
            return;
        }

        let cancelled = false;

        const loadScenarios = async () => {
            try {
                const data = await evService.getEVScenarios(projectId, currentUser);
                if (cancelled) return;
                setScenarios(data);

                // Auto-create a "Default" scenario if project has none
                if (data.length === 0 && autoCreatedForRef.current !== projectId) {
                    autoCreatedForRef.current = projectId;
                    try {
                        const scenario = await evService.createEVScenario(projectId, 'Default', currentUser);
                        if (cancelled) return;
                        setScenarios([scenario]);
                    } catch (createErr) {
                        console.error('Failed to auto-create default scenario:', createErr);
                    }
                }
            } catch (err) {
                console.error('Failed to load scenarios:', err);
            }
        };

        loadScenarios();

        return () => { cancelled = true; };
    }, [projectId, currentUser, currentProject]);

    // CRUD
    const createScenario = useCallback(async () => {
        if (!currentUser || !projectId) return;
        try {
            const scenario = await evService.createEVScenario(projectId, 'New Scenario', currentUser);
            // Seed the new scenario with the project's fleet for backward compatibility
            const seeded = {
                ...scenario,
                fleet: scenario.fleet || currentProject?.fleet || [],
            };
            setScenarios(prev => [seeded, ...prev]);
            setSelectedScenario(seeded);
        } catch (err) {
            throw err;
        }
    }, [currentUser, projectId, currentProject]);

    const deleteScenario = useCallback(async (scenarioId) => {
        if (!currentUser || !projectId) return;
        if (!window.confirm('Delete this scenario?')) return;
        try {
            await evService.deleteEVScenario(projectId, scenarioId, currentUser);
            setScenarios(prev => prev.filter(s => s.id !== scenarioId));
            // FIX B3: Use functional check to avoid stale selectedScenario reference
            setSelectedScenario(prev => prev?.id === scenarioId ? null : prev);
        } catch (err) {
            throw err;
        }
    }, [currentUser, projectId]);

    const duplicateScenario = useCallback(async (scenarioId) => {
        if (!currentUser || !projectId) return;
        try {
            const scenario = await evService.duplicateEVScenario(projectId, scenarioId, currentUser);
            setScenarios(prev => [scenario, ...prev]);
        } catch (err) {
            throw err;
        }
    }, [currentUser, projectId]);

    // Selection — inherit project fleet if scenario has none
    const selectScenario = useCallback((scenario) => {
        const projectFleet = currentProject?.fleet || [];
        if ((!scenario.fleet || scenario.fleet.length === 0) && projectFleet.length > 0) {
            setSelectedScenario({ ...scenario, fleet: projectFleet });
            setSaveStatus('dirty');
        } else {
            setSelectedScenario(scenario);
            setSaveStatus('idle');
        }
        setSaveError(null);
    }, [currentProject]);

    const deselectScenario = useCallback(() => {
        setSelectedScenario(null);
        setSaveStatus('idle');
        setSaveError(null);
    }, []);

    // Change counter — increments on every edit so the autosave timer resets
    const [changeCount, setChangeCount] = useState(0);

    // FIX B2: updateScenario uses functional updater with NO selectedScenario in deps
    const updateScenario = useCallback((updates) => {
        setSelectedScenario(prev => {
            if (!prev) return prev;
            return { ...prev, ...updates };
        });
        setSaveStatus('dirty');
        setChangeCount(c => c + 1);
    }, []);

    // Convert frontend time format (HH:MM) to backend integer hours
    const prepareVehicleForBackend = (vehicle) => {
        const v = { ...vehicle };
        // Convert HH:MM times to integer hours for backend
        if (v.departure_time) {
            v.departure_hour = parseInt(v.departure_time.split(':')[0], 10);
        }
        if (v.return_time) {
            v.arrival_hour = parseInt(v.return_time.split(':')[0], 10);
        }
        // Convert weekly schedule times — keep both frontend (HH:MM) and backend (integer) formats
        // so VehicleCard can read departure_time/return_time on reload from Firestore
        if (v.schedule_mode === 'weekly' && v.weekly_schedule) {
            const ws = {};
            for (const [day, data] of Object.entries(v.weekly_schedule)) {
                if (data === null) {
                    ws[day] = null;
                } else {
                    ws[day] = {
                        departure_time: data.departure_time || '06:00',
                        return_time: data.return_time || '18:00',
                        daily_miles: data.daily_miles || 100,
                        departure_hour: parseInt((data.departure_time || '06:00').split(':')[0], 10),
                        arrival_hour: parseInt((data.return_time || '18:00').split(':')[0], 10),
                    };
                }
            }
            v.weekly_schedule = ws;
        }
        return v;
    };

    // Save
    const saveScenario = useCallback(async () => {
        if (!currentUser || !projectId) return;

        // Read current selectedScenario via functional pattern
        let scenarioToSave = null;
        setSelectedScenario(prev => {
            scenarioToSave = prev;
            return prev;
        });

        if (!scenarioToSave) return;

        try {
            setSaveStatus('saving');
            setSaveError(null);
            // Prepare fleet for backend (time format conversion)
            const scenarioPayload = { ...scenarioToSave };
            if (scenarioPayload.fleet) {
                scenarioPayload.fleet = scenarioPayload.fleet.map(prepareVehicleForBackend);
            }
            const updated = await evService.updateEVScenario(
                projectId,
                scenarioToSave.id,
                scenarioPayload,
                currentUser
            );
            setSelectedScenario(updated);
            setScenarios(prev => prev.map(s => s.id === updated.id ? updated : s));
            setSaveStatus('saved');
            setLastSavedAt(new Date().toISOString());
        } catch (err) {
            setSaveStatus('error');
            setSaveError(err.message);
        }
    }, [currentUser, projectId]);

    // Debounced autosave: auto-save 1.5s after last edit
    const autosaveTimerRef = useRef(null);
    useEffect(() => {
        if (changeCount === 0) return; // skip initial mount
        autosaveTimerRef.current = setTimeout(() => {
            saveScenario();
        }, 1500);
        return () => clearTimeout(autosaveTimerRef.current);
    }, [changeCount, saveScenario]);

    // Save on unmount if dirty
    const saveStatusRef = useRef(saveStatus);
    saveStatusRef.current = saveStatus;
    const saveScenarioRef = useRef(saveScenario);
    saveScenarioRef.current = saveScenario;
    useEffect(() => {
        return () => {
            if (saveStatusRef.current === 'dirty') {
                saveScenarioRef.current();
            }
        };
    }, []);

    // Simulate single scenario
    const simulateScenario = useCallback(async (scenarioId) => {
        if (!currentUser || !projectId) return;
        try {
            setSimulatingScenarioId(scenarioId);
            const results = await evService.simulateScenario(projectId, scenarioId, currentUser);

            // Capture SOC trajectories per strategy before they get stripped on reload
            const strategies = results?.strategies || {};
            const socByStrategy = {};
            for (const [key, strat] of Object.entries(strategies)) {
                if (strat?.soc_trajectories) {
                    socByStrategy[key] = strat.soc_trajectories;
                }
            }
            if (Object.keys(socByStrategy).length > 0) {
                socDataRef.current[scenarioId] = socByStrategy;
            }

            setScenarios(prev => prev.map(s => s.id === scenarioId ? { ...s, results } : s));
            // FIX B3: functional updater avoids stale selectedScenario
            setSelectedScenario(prev => {
                if (prev?.id === scenarioId) return { ...prev, results };
                return prev;
            });
        } finally {
            setSimulatingScenarioId(null);
        }
    }, [currentUser, projectId]);

    const getSocTrajectories = useCallback((scenarioId, strategyKey) => {
        const byStrategy = socDataRef.current[scenarioId];
        if (!byStrategy) return null;
        if (strategyKey && byStrategy[strategyKey]) return byStrategy[strategyKey];
        // Fallback: cost_optimized → demand_limit → opportunity → first available
        return byStrategy.cost_optimized || byStrategy.demand_limit || byStrategy.opportunity
            || Object.values(byStrategy)[0] || null;
    }, []);

    return {
        // State
        scenarios,
        selectedScenario,
        simulatingScenarioId,
        saveStatus,
        lastSavedAt,
        saveError,

        // Expose setter for SSE batch updates from Results section
        setScenarios,

        // Actions
        createScenario,
        deleteScenario,
        duplicateScenario,

        // Selection
        selectScenario,
        deselectScenario,

        // Edit
        updateScenario,

        // Save
        saveScenario,

        // Simulate
        simulateScenario,

        // SOC data
        getSocTrajectories,
    };
}
